import { api } from '@packages/backend/convex/_generated/api';
import { type DayReportCvx, formatCents, getUserErrorMessage } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { format } from 'date-fns';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { SuperAdminOnly } from '@/components/SuperAdminOnly';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { cn } from '@/utils/cn';

export const Route = createFileRoute('/r/$restaurantId/day-reports')({
  component: Component,
});

function Component() {
  const { restaurantId } = Route.useParams();
  const {
    data: dayReports,
    isError,
    isPending,
  } = useQuery(api.dayReports.listByRestaurantId, { restaurantId });

  if (isError) return null; // Handled in parent layout

  return (
    <div className="p-6 min-h-[calc(100vh-(--spacing(16)))] flex-1 flex-col gap-4 bg-muted/60 dark:bg-muted/20">
      {isPending ? (
        <LoadingIcon />
      ) : (
        <>
          <SalesChart dayReports={dayReports} />
          <Card>
            <CardHeader>
              <CardTitle>Day reports</CardTitle>
            </CardHeader>
            <CardContent>
              <DayReportsTable dayReports={dayReports} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

const SalesChart = ({ dayReports }: { dayReports: DayReportCvx[] }) => {
  // Sort the reports by date
  const sortedReports = [...dayReports].sort((a, b) => {
    const dateA = new Date(a.year, a.month - 1, a.day);
    const dateB = new Date(b.year, b.month - 1, b.day);
    return dateA.getTime() - dateB.getTime();
  });

  // Prepare data for the chart
  const chartData = sortedReports.map((report) => {
    const date = new Date(report.year, report.month - 1, report.day);
    return {
      date: format(date, 'yyyy-MM-dd'),
      totalAmount: report.totalAmount,
    };
  });

  // Define chart config
  const chartConfig = {
    totalAmount: {
      label: 'Total Sales',
      color: 'var(--chart-1)',
    },
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Sales over time</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No sales data available
          </div>
        ) : (
          <div className="h-[300px] w-full">
            <ChartContainer config={chartConfig} className="h-full w-full">
              <BarChart accessibilityLayer data={chartData}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                />
                <YAxis
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  tickFormatter={(value) => formatCents(value, '$')}
                />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) =>
                        typeof value === 'number' ? formatCents(value, '$') : String(value)
                      }
                    />
                  }
                />
                <Bar dataKey="totalAmount" fill="var(--chart-1)" radius={4} name="Total Sales" />
              </BarChart>
            </ChartContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DayReportsTable = ({ dayReports }: { dayReports: DayReportCvx[] }) => {
  const convex = useConvex();

  const handleDelete = async (dayReportId: string) => {
    try {
      await convex.mutation(api.dayReports.deleteDayReport, {
        dayReportId,
      });
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error("Couldn't delete", {
        description: getUserErrorMessage(error),
      });
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Orders</TableHead>
          <TableHead>EFTPOS</TableHead>
          <TableHead>Cash</TableHead>
          <TableHead>Online</TableHead>
          <TableHead>Total</TableHead>
          <SuperAdminOnly>
            <TableHead />
          </SuperAdminOnly>
        </TableRow>
      </TableHeader>
      <TableBody>
        {dayReports.map((r) => {
          return (
            <TableRow
              key={r._id}
              className={cn(r.totalAmount === 0 && 'text-neutral-300 dark:text-neutral-500')}
            >
              <TableCell className="py-0">
                <div className="font-medium">
                  {format(new Date(r.year, r.month - 1, r.day), 'yyyy-MM-dd')}
                </div>
              </TableCell>
              <TableCell className="py-0">{`${r.ordersCount}`}</TableCell>
              <TableCell className="py-0">
                {`${formatCents(r.eftposAmount, '$')} (${r.eftposPaymentsCount})`}
              </TableCell>
              <TableCell className="py-0">{`${formatCents(r.cashAmount, '$')} (${r.cashPaymentsCount})`}</TableCell>
              <TableCell className="py-0">
                {`${formatCents(r.onlineAmount, '$')} (${r.onlinePaymentsCount})`}
              </TableCell>
              <TableCell className="py-0">{`${formatCents(r.totalAmount, '$')} (${r.totalPaymentsCount})`}</TableCell>

              <SuperAdminOnly>
                <TableCell className="py-0">
                  <ActionDropdown onDelete={() => handleDelete(r._id)} />
                </TableCell>
              </SuperAdminOnly>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
